import React, { useState } from 'react';
import { useAppState } from '../hooks/useAppState';
import { apiService } from '../services/api';
import { getConfig } from '../services/configManager';
import { isValidEmail, getCleanPhoneNumber } from '../utils/validation';
import BackButton from '../components/BackButton';
import PhoneInput from '../components/PhoneInput';

interface FormErrors {
  fullName?: string;
  email?: string;
  reason?: string;
  message?: string;
}

const MESSAGE_MAX_LENGTH = 2048;

const hasAtLeastTwoWords = (value: string): boolean =>
  value.trim().split(/\s+/).filter(Boolean).length >= 2;

const ContactUsPage: React.FC = () => {
  const { state, setError, setStep } = useAppState();
  const reasons = getConfig().FEATURES.ContactUs.Reasons;

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const clearFieldError = (field: keyof FormErrors) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): FormErrors => {
    const nextErrors: FormErrors = {};
    const trimmedFullName = fullName.trim();

    if (!trimmedFullName) {
      nextErrors.fullName = 'Full name is required';
    } else if (!hasAtLeastTwoWords(trimmedFullName)) {
      nextErrors.fullName = 'Please enter your first and last name.';
    }

    if (!email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!isValidEmail(email.trim())) {
      nextErrors.email = 'Please enter a valid email address';
    }

    if (!reason.trim()) {
      nextErrors.reason = 'Please select an inquiry reason.';
    }

    if (!message.trim()) {
      nextErrors.message = 'Please enter your message.';
    }

    return nextErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      await apiService.sendContactUs({
        contact: {
          name: fullName.trim() || null,
          email: email.trim(),
          phone: phone.trim() ? getCleanPhoneNumber(phone) : null,
        },
        payload: {
          reason: reason.trim(),
          subject: reason.trim(),
          message: message.trim(),
        },
      });
      setIsSubmitted(true);
    } catch (error) {
      setError(error instanceof Error ? error.message : "Your message couldn't be sent. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = isSubmitting || !fullName.trim() || !email.trim() || !reason.trim() || !message.trim();

  return (
    <div className="flex-1 flex flex-col p-5">
      <div className="mb-4">
        <BackButton targetStep={state.returnStep} />
      </div>

      <div className="flex-1 flex items-center justify-center">
        <div className="container-card w-full max-w-sm">
          {isSubmitted ? (
            <div className="flex flex-col items-center justify-center py-8 gap-4 text-center">
              <svg className="w-16 h-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h1 className="text-title font-extrabold text-black">Message sent!</h1>
              <p className="text-md text-text-disabled">We'll get back to you shortly.</p>
              <button onClick={() => setStep(state.returnStep)} className="btn-primary w-full mt-2">
                Go Back
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-title font-extrabold text-black text-center mb-2">
                Contact Us
              </h1>
              <p className="text-md text-text-disabled text-center mb-8">How can we help?</p>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-black text-md">Full Name <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => { setFullName(e.target.value); clearFieldError('fullName'); }}
                    placeholder="Enter your full name"
                    className={`input-field ${errors.fullName ? 'border-red-500' : ''}`}
                    maxLength={100}
                  />
                  {errors.fullName && <span className="text-red-500 text-sm">{errors.fullName}</span>}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-black text-md">Email <span className="required-asterisk">*</span></label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }}
                    placeholder="example@mail.com"
                    className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                    maxLength={254}
                  />
                  {errors.email && <span className="text-red-500 text-sm">{errors.email}</span>}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-black text-md">Phone Number</label>
                  <PhoneInput value={phone} onChange={setPhone} />
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-black text-md">Inquiry Reason <span className="required-asterisk">*</span></label>
                  <select
                    value={reason}
                    onChange={(e) => { setReason(e.target.value); clearFieldError('reason'); }}
                    className={`input-field ${errors.reason ? 'border-red-500' : ''}`}
                  >
                    <option value="" disabled>Choose</option>
                    {reasons.map((inquiryReason) => (
                      <option key={inquiryReason} value={inquiryReason}>{inquiryReason}</option>
                    ))}
                  </select>
                  {errors.reason && <span className="text-red-500 text-sm">{errors.reason}</span>}
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-black text-md">Message <span className="required-asterisk">*</span></label>
                  <textarea
                    value={message}
                    onChange={(e) => { setMessage(e.target.value); clearFieldError('message'); }}
                    placeholder="How can we help you?"
                    rows={5}
                    className={`input-field resize-y ${errors.message ? 'border-red-500' : ''}`}
                    maxLength={MESSAGE_MAX_LENGTH}
                  />
                  {errors.message && <span className="text-red-500 text-sm">{errors.message}</span>}
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={isSubmitDisabled}
                  className="btn-primary w-full mt-2 flex items-center justify-center gap-2"
                >
                  {isSubmitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  )}
                  {isSubmitting ? 'Sending...' : 'Send'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactUsPage;
